/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Anchor, Text } from '@/shared/ui';
import type { MissingAdSetting } from '@/shared/utils/ad-readiness';

type T = (key: string, opts?: Record<string, unknown>) => string;

/** The settings page where the agency fills in what its ads need. */
export function adSettingsHref(agencyId: string): string {
  return `/app/agencies/${agencyId}/settings?section=facebook`;
}

/**
 * Tells the agency what it must fill in before it can create a Lead Ad or a
 * boost, and links to where it does. Housien supplies none of these values: they
 * run on the agency's own Meta account and money, and what leads see is the
 * agency's responsibility, so the agency sets them first.
 *
 * @param props.kind - Which ad the agency is trying to create.
 * @param props.missing - What is still missing (never empty when this is shown).
 * @param props.settingsHref - Where the settings are.
 * @param props.testId - Test id of the notice.
 * @returns The notice, to render in place of the ad's controls.
 */
export function AdSetupNotice({
  t, kind, missing, settingsHref, testId,
}: {
  t: T;
  kind: 'leadAd' | 'boost';
  missing: MissingAdSetting[];
  settingsHref: string;
  testId: string;
}) {
  const items = missing.map((item) => t(`adSettings.missing.${item}`)).join(', ');
  return (
    <div className="flex flex-col gap-(--space-1-5)" data-testid={testId}>
      <Text className="m-0 text-xs leading-relaxed text-[var(--status-warning-text)]">
        {t(kind === 'boost' ? 'adSettings.boostNeedsSetup' : 'adSettings.leadAdNeedsSetup', { items })}
      </Text>
      <Anchor
        href={settingsHref}
        underline="never"
        className="self-start text-xs font-semibold text-[var(--seed-base)] underline"
        data-testid={`${testId}-link`}
      >
        {t('adSettings.configure')}
      </Anchor>
    </div>
  );
}
